/**
 * Hook para integración con wallets Web3 (MetaMask, Trust Wallet, Coinbase Wallet, etc.)
 * Soporta Ethereum, BSC, Polygon y otras redes EVM
 * Detecta automáticamente cualquier wallet compatible con EIP-1193
 */

import { useState, useEffect, useCallback } from 'react'
import { ethers } from 'ethers'
import { toast } from 'sonner'
import { useLanguage } from './use-language'

export type NetworkType = 'ethereum' | 'bsc' | 'bsc_testnet' | 'polygon'

interface WalletState {
  isConnected: boolean
  address: string | null
  network: NetworkType | null
  balance: string | null
  chainId: number | null
}

interface SendPaymentResult {
  success: boolean
  txHash?: string
  blockNumber?: number
  error?: string
}

// Configuración de redes
const NETWORK_CONFIG = {
  ethereum: {
    chainId: '0x1',
    chainName: 'Ethereum Mainnet',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.infura.io/v3/'],
    blockExplorerUrls: ['https://etherscan.io']
  },
  bsc: {
    chainId: '0x38',
    chainName: 'BNB Smart Chain',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrls: ['https://bsc-dataseed.binance.org/'],
    blockExplorerUrls: ['https://bscscan.com']
  },
  bsc_testnet: {
    chainId: '0x61',
    chainName: 'BSC Testnet',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545/'],
    blockExplorerUrls: ['https://testnet.bscscan.com']
  },
  polygon: {
    chainId: '0x89',
    chainName: 'Polygon Mainnet',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    rpcUrls: ['https://polygon-rpc.com/'],
    blockExplorerUrls: ['https://polygonscan.com']
  }
}

// Direcciones de contratos USDT
const USDT_CONTRACTS = {
  ethereum: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  bsc: '0x55d398326f99059fF775485246999027B3197955',
  bsc_testnet: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd', // BSC Testnet USDT
  polygon: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F'
}

// ABI mínimo para USDT (ERC20)
const USDT_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
  'function balanceOf(address account) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)'
]

export function useWallet() {
  const { language } = useLanguage()
  const [wallet, setWallet] = useState<WalletState>({
    isConnected: false,
    address: null,
    network: null,
    balance: null,
    chainId: null
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Verificar si hay alguna wallet instalada (MetaMask, Trust Wallet, Coinbase, etc.)
  const isWalletInstalled = useCallback(() => {
    return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined'
  }, [])

  // Verificar específicamente MetaMask (para mostrar mensajes específicos)
  const isMetaMaskInstalled = useCallback(() => {
    return typeof window !== 'undefined' && 
           typeof window.ethereum !== 'undefined' &&
           (window.ethereum as any).isMetaMask === true
  }, [])

  // Detectar red actual
  const detectNetwork = useCallback((chainId: number): NetworkType | null => {
    const chainIdHex = `0x${chainId.toString(16)}`
    if (chainIdHex === NETWORK_CONFIG.ethereum.chainId) return 'ethereum'
    if (chainIdHex === NETWORK_CONFIG.bsc.chainId) return 'bsc'
    if (chainIdHex === NETWORK_CONFIG.bsc_testnet.chainId) return 'bsc_testnet'
    if (chainIdHex === NETWORK_CONFIG.polygon.chainId) return 'polygon'
    return null
  }, [])

  // Conectar wallet con retry automático
  const connectWallet = useCallback(async (retryCount = 0): Promise<boolean> => {
    if (!isWalletInstalled()) {
      const errorMsg = language === 'en' 
        ? 'No wallet detected. Please install MetaMask, Trust Wallet or Coinbase Wallet'
        : 'No se detectó ninguna wallet. Por favor instala MetaMask, Trust Wallet o Coinbase Wallet'
      setError(errorMsg)
      toast.error(errorMsg)
      return false
    }

    setLoading(true)
    setError(null)

    try {
      if (!window.ethereum) {
        throw new Error('Wallet no disponible')
      }
      // Pequeño delay para asegurar que MetaMask esté listo
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      const provider = new ethers.BrowserProvider(window.ethereum)
      const accounts = await provider.send('eth_requestAccounts', [])
      
      if (accounts.length === 0) {
        throw new Error('No se seleccionó ninguna cuenta')
      }

      // Esperar un momento para que MetaMask procese
      await new Promise(resolve => setTimeout(resolve, 300))

      const signer = await provider.getSigner()
      const address = await signer.getAddress()
      const network = await provider.getNetwork()
      const balance = await provider.getBalance(address)

      const detectedNetwork = detectNetwork(Number(network.chainId))

      setWallet({
        isConnected: true,
        address,
        network: detectedNetwork,
        balance: ethers.formatEther(balance),
        chainId: Number(network.chainId)
      })

      toast.success(language === 'en' ? 'Wallet connected successfully' : 'Wallet conectada exitosamente')
      return true
    } catch (err: any) {
      const errorMsg = err.message || (language === 'en' ? 'Error connecting wallet' : 'Error al conectar wallet')
      setError(errorMsg)
      
      // Retry automático una vez si falla (excepto si el usuario canceló)
      if (retryCount === 0 && err.code !== 4001) {
        console.log('🔄 Reintentando conexión de wallet...')
        setLoading(false)
        await new Promise(resolve => setTimeout(resolve, 500))
        return connectWallet(1)
      }
      
      if (err.code === 4001) {
        toast.error(language === 'en' ? 'Connection cancelled by user' : 'Conexión cancelada por el usuario')
      } else {
        toast.error(errorMsg)
      }
      
      return false
    } finally {
      setLoading(false)
    }
  }, [isWalletInstalled, detectNetwork, language])

  // Cambiar a red específica
  const switchNetwork = useCallback(async (network: NetworkType): Promise<boolean> => {
    if (!isWalletInstalled()) {
      return false
    }

    try {
      if (!window.ethereum) {
        return false
      }
      const config = NETWORK_CONFIG[network]
      
      // Intentar cambiar de red
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: config.chainId }]
        })
        return true
      } catch (switchError: any) {
        // Si la red no existe, agregarla
        if (switchError.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [config]
          })
          return true
        }
        throw switchError
      }
    } catch (err: any) {
      console.error('Error switching network:', err)
      toast.error(`Error al cambiar a ${network}: ${err.message}`)
      return false
    }
  }, [isWalletInstalled])

  // Enviar pago (soporta USDT token y BNB/ETH nativo)
  const sendPayment = useCallback(async (
    toAddress: string,
    amountUSD: number,
    network: NetworkType,
    currency?: string // 'BNB', 'ETH', 'USDT (BEP20)', etc.
  ): Promise<SendPaymentResult> => {
    if (!wallet.isConnected) {
      return {
        success: false,
        error: 'Wallet no conectada'
      }
    }

    setLoading(true)
    setError(null)

    try {
      // Cambiar a la red correcta si es necesario
      // Para testing, usar bsc_testnet si está disponible
      const targetNetwork = network === 'bsc' && wallet.network === 'bsc_testnet' ? 'bsc_testnet' : network
      
      if (wallet.network !== targetNetwork && targetNetwork !== 'bsc_testnet') {
        const switched = await switchNetwork(targetNetwork)
        if (!switched) {
          return {
            success: false,
            error: 'No se pudo cambiar a la red requerida'
          }
        }
        // Esperar un momento para que MetaMask actualice
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      if (!window.ethereum) {
        return {
          success: false,
          error: 'Wallet no disponible'
        }
      }
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()

      // Detectar si es pago nativo (BNB, ETH) o token (USDT)
      const isNativePayment = currency === 'BNB' || currency === 'ETH'
      let receipt: any
      
      if (isNativePayment) {
        // Pago nativo (BNB o ETH)
        console.log(`📤 Enviando ${currency} nativo...`)
        console.log('  To:', toAddress)
        console.log('  Amount:', amountUSD, currency)
        console.log('  Network:', network)
        
        // Convertir a wei (18 decimals para BNB/ETH)
        const amount = ethers.parseEther(amountUSD.toString())
        
        // Verificar balance nativo
        const balance = await provider.getBalance(wallet.address!)
        if (balance < amount) {
          const balanceFormatted = ethers.formatEther(balance)
          toast.error(
            language === 'en'
              ? `Insufficient ${currency} balance. You have ${balanceFormatted} ${currency}, need ${amountUSD} ${currency}.`
              : `Balance insuficiente de ${currency}. Tienes ${balanceFormatted} ${currency}, necesitas ${amountUSD} ${currency}.`
          )
          return {
            success: false,
            error: `Balance insuficiente. Necesitas ${amountUSD} ${currency}`
          }
        }
        
        toast.info(language === 'en' ? 'Confirming transaction in your wallet...' : 'Confirmando transacción en tu wallet...')
        
        // Enviar transacción nativa
        const tx = await signer.sendTransaction({
          to: toAddress,
          value: amount
        })
        
        console.log('✅ Transacción enviada, hash:', tx.hash)
        console.log('⏳ Esperando confirmación...')
        
        toast.info(language === 'en' ? 'Waiting for confirmation...' : 'Esperando confirmación...')
        receipt = await tx.wait()
        console.log('✅ Transacción confirmada:', receipt)
      } else {
        // Pago con token USDT
        const contractNetwork = network === 'bsc_testnet' ? 'bsc_testnet' : network
        const usdtAddress = USDT_CONTRACTS[contractNetwork] || USDT_CONTRACTS[network]
        
        if (!usdtAddress) {
          return {
            success: false,
            error: `USDT contract not found for network ${network}`
          }
        }
        
        const usdtContract = new ethers.Contract(usdtAddress, USDT_ABI, signer)

        // Obtener decimals y calcular amount
        const decimals = await usdtContract.decimals()
        const amount = ethers.parseUnits(amountUSD.toString(), decimals)

        // Verificar balance USDT
        const balance = await usdtContract.balanceOf(wallet.address!)
        if (balance < amount) {
          const balanceFormatted = ethers.formatUnits(balance, decimals)
          toast.error(
            language === 'en'
              ? `Insufficient USDT balance. You have ${balanceFormatted} USDT, need ${amountUSD} USDT.`
              : `Balance insuficiente de USDT. Tienes ${balanceFormatted} USDT, necesitas ${amountUSD} USDT.`
          )
          return {
            success: false,
            error: `Balance insuficiente. Necesitas ${amountUSD} USDT`
          }
        }

        // Enviar transacción USDT (esto abrirá MetaMask automáticamente)
        console.log('📤 Enviando transacción USDT...')
        console.log('  To:', toAddress)
        console.log('  Amount:', amount.toString())
        console.log('  Network:', contractNetwork)
        
        toast.info(language === 'en' ? 'Confirming transaction in your wallet...' : 'Confirmando transacción en tu wallet...')
        
        // Esto debería abrir MetaMask automáticamente
        const tx = await usdtContract.transfer(toAddress, amount)
        console.log('✅ Transacción enviada, hash:', tx.hash)
        console.log('⏳ Esperando confirmación...')
        
        toast.info(language === 'en' ? 'Waiting for confirmation...' : 'Esperando confirmación...')
        receipt = await tx.wait()
        console.log('✅ Transacción confirmada:', receipt)
      }

      if (receipt && receipt.status === 1) {
        toast.success(language === 'en' ? 'Payment sent successfully!' : '¡Pago enviado exitosamente!')
        return {
          success: true,
          txHash: receipt.hash,
          blockNumber: receipt.blockNumber
        }
      } else {
        return {
          success: false,
          error: language === 'en' ? 'Transaction failed' : 'La transacción falló'
        }
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Error al enviar pago'
      setError(errorMsg)
      
      if (err.code === 4001) {
        toast.error(language === 'en' ? 'Transaction cancelled by user' : 'Transacción cancelada por el usuario')
        return {
          success: false,
          error: language === 'en' ? 'Transaction cancelled' : 'Transacción cancelada'
        }
      }
      
      // Mensajes de error más específicos
      if (err.message?.includes('insufficient funds') || err.message?.includes('balance')) {
        const errorMsg = language === 'en' 
          ? 'Insufficient balance. Please check your wallet balance.' 
          : 'Balance insuficiente. Por favor verifica el balance de tu wallet.'
        toast.error(errorMsg)
        return {
          success: false,
          error: errorMsg
        }
      }
      
      toast.error(errorMsg)
      return {
        success: false,
        error: errorMsg
      }
    } finally {
      setLoading(false)
    }
  }, [wallet, switchNetwork, language])

  // Escuchar cambios de cuenta
  useEffect(() => {
    if (!isWalletInstalled()) return

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        setWallet({
          isConnected: false,
          address: null,
          network: null,
          balance: null,
          chainId: null
        })
      } else {
        connectWallet()
      }
    }

    const handleChainChanged = () => {
      // Recargar página cuando cambia la red (MetaMask recomienda esto)
      window.location.reload()
    }

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged)
      window.ethereum.on('chainChanged', handleChainChanged)

      return () => {
        if (window.ethereum) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
          window.ethereum.removeListener('chainChanged', handleChainChanged)
        }
      }
    }
  }, [isWalletInstalled, connectWallet])

  return {
    wallet,
    loading,
    error,
    connectWallet,
    sendPayment,
    switchNetwork,
    isWalletInstalled,
    isMetaMaskInstalled // Mantener para compatibilidad
  }
}

// Extender Window interface para TypeScript
declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: any[] }) => Promise<any>
      on: (event: string, handler: (...args: any[]) => void) => void
      removeListener: (event: string, handler: (...args: any[]) => void) => void
    }
  }
}

